// PieChartDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { PieChart } from './chart';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ApiURL from '../Api/Api';
import { Link, useNavigate } from 'react-router-dom';
import WorkerNotifications from './WorkerNotifications';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  BellIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChartPieIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

ChartJS.register(ArcElement, Tooltip, ChartDataLabels);

/* ─── Donut chart wrapper — fixes the percentage bug ─────────────────── */
const DonutCard = ({ title, credit, debit, activeDataset, subtitle, accent }) => {
  // FIX: coerce to number always
  const c = Number(credit) || 0;
  const d = Number(debit) || 0;
  const total = c + d;

  const creditPct = total > 0 ? Math.round((c / total) * 100) : 0;
  const debitPct = total > 0 ? Math.round((d / total) * 100) : 0;

  const getData = () => {
    if (activeDataset === 'credit') {
      return {
        labels: ['Credit'],
        datasets: [{ data: [c || 1], backgroundColor: ['#10b981'], borderWidth: 0, hoverOffset: 4 }],
      };
    }
    if (activeDataset === 'debit') {
      return {
        labels: ['Debit'],
        datasets: [{ data: [d || 1], backgroundColor: ['#f43f5e'], borderWidth: 0, hoverOffset: 4 }],
      };
    }
    if (total === 0) {
      return {
        labels: ['No Data'],
        datasets: [{ data: [1], backgroundColor: ['#e2e8f0'], borderWidth: 0, hoverOffset: 0 }],
      };
    }
    return {
      labels: ['Credit', 'Debit'],
      datasets: [{ data: [c, d], backgroundColor: ['#10b981', '#f43f5e'], borderWidth: 0, hoverOffset: 4 }],
    };
  };

  const formatCurrency = (v) =>
    v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '₹0';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.92)',
        titleFont: { size: 12, family: "'DM Sans', sans-serif", weight: '600' },
        bodyFont: { size: 11, family: "'DM Sans', sans-serif" },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            if (total === 0) return 'No data';
            const val = ctx.raw;
            const pct = Math.round((val / total) * 100);
            return ` ${ctx.label}: ${formatCurrency(val)} (${pct}%)`;
          },
        },
      },
      datalabels: { display: false },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
      style={{ minHeight: 220 }}
    >
      {/* top accent stripe */}
      <div className={`h-1 w-full ${accent || 'bg-gradient-to-r from-indigo-500 to-violet-500'}`} />

      <div className="flex flex-col flex-1 p-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">{title}</p>
        {subtitle && <p className="text-[10px] text-slate-400 mb-3">{subtitle}</p>}

        {/* donut */}
        <div className="relative flex-1 flex items-center justify-center" style={{ minHeight: 120 }}>
          <div className="w-full" style={{ height: 120 }}>
            <PieChart data={getData()} options={options} />
          </div>
          {/* centre label */}
          {total > 0 && activeDataset === 'both' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] font-semibold text-slate-400">Total</span>
              <span className="text-[13px] font-bold text-slate-700">{formatCurrency(total)}</span>
            </div>
          )}
        </div>

        {/* legend pills */}
        {activeDataset === 'both' && total > 0 ? (
          <div className="flex gap-2 mt-3 flex-wrap justify-center">
            <div className="flex items-center gap-1.5 bg-emerald-50 rounded-full px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold text-emerald-700">{creditPct}% · {formatCurrency(c)}</span>
            </div>
            <div className="flex items-center gap-1.5 bg-rose-50 rounded-full px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />
              <span className="text-[10px] font-semibold text-rose-700">{debitPct}% · {formatCurrency(d)}</span>
            </div>
          </div>
        ) : activeDataset === 'credit' ? (
          <div className="flex justify-center mt-3">
            <div className="flex items-center gap-1.5 bg-emerald-50 rounded-full px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-[10px] font-semibold text-emerald-700">Credit · {formatCurrency(c)}</span>
            </div>
          </div>
        ) : activeDataset === 'debit' ? (
          <div className="flex justify-center mt-3">
            <div className="flex items-center gap-1.5 bg-rose-50 rounded-full px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              <span className="text-[10px] font-semibold text-rose-700">Debit · {formatCurrency(d)}</span>
            </div>
          </div>
        ) : (
          <p className="text-center text-[11px] text-slate-400 mt-3">No transactions</p>
        )}
      </div>
    </motion.div>
  );
};

/* ─── Net Balance donut ───────────────────────────────────────────────── */
const NetBalanceDonut = ({ balance }) => {
  const isPos = balance >= 0;
  const abs = Math.abs(Number(balance) || 0);
  const formatCurrency = (v) => `₹${Number(v).toLocaleString('en-IN')}`;

  const data = {
    labels: [isPos ? 'Surplus' : 'Deficit'],
    datasets: [{
      data: [abs || 1],
      backgroundColor: [isPos ? '#10b981' : '#f43f5e'],
      borderWidth: 0,
      hoverOffset: 4,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.92)',
        padding: 10,
        cornerRadius: 8,
        callbacks: { label: () => ` ${formatCurrency(abs)} (${isPos ? 'Surplus' : 'Deficit'})` },
      },
      datalabels: { display: false },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
      style={{ minHeight: 220 }}
    >
      <div className={`h-1 w-full ${isPos ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-rose-400 to-pink-500'}`} />
      <div className="flex flex-col flex-1 p-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Net Balance</p>

        <div className="relative flex-1 flex items-center justify-center" style={{ minHeight: 120 }}>
          <div className="w-full" style={{ height: 120 }}>
            <PieChart data={data} options={options} />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className={`text-[10px] font-bold uppercase ${isPos ? 'text-emerald-600' : 'text-rose-600'}`}>
              {isPos ? 'Surplus' : 'Deficit'}
            </span>
          </div>
        </div>

        <div className="mt-3 text-center">
          <p className={`text-base font-bold ${isPos ? 'text-emerald-600' : 'text-rose-600'}`}>
            {formatCurrency(abs)}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Status donut ────────────────────────────────────────────────────── */
const StatusDonut = ({ statusData, onNavigate }) => {
  const [inProg, approved, rejected] = statusData.datasets[0].data;
  const total = inProg + approved + rejected;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.92)',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.raw} requests`,
        },
      },
      datalabels: { display: false },
    },
    onClick: (_, elements) => {
      if (elements.length > 0) {
        const status = ['inprogress', 'approved', 'rejected'][elements[0].index];
        onNavigate(status);
      }
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col"
      style={{ minHeight: 220 }}
    >
      <div className="h-1 w-full bg-gradient-to-r from-amber-400 to-orange-400" />
      <div className="flex flex-col flex-1 p-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Request Status</p>

        <div className="relative flex-1 flex items-center justify-center" style={{ minHeight: 120 }}>
          <div className="w-full" style={{ height: 120 }}>
            <PieChart data={statusData} options={options} />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[10px] font-semibold text-slate-400">Total</span>
            <span className="text-[13px] font-bold text-slate-700">{total}</span>
          </div>
        </div>

        <div className="flex gap-2 mt-3 justify-center flex-wrap">
          <div className="flex items-center gap-1 bg-amber-50 rounded-full px-2 py-0.5">
            <ClockIcon className="w-3 h-3 text-amber-500" />
            <span className="text-[10px] font-semibold text-amber-700">{inProg}</span>
          </div>
          <div className="flex items-center gap-1 bg-emerald-50 rounded-full px-2 py-0.5">
            <CheckCircleIcon className="w-3 h-3 text-emerald-500" />
            <span className="text-[10px] font-semibold text-emerald-700">{approved}</span>
          </div>
          <div className="flex items-center gap-1 bg-rose-50 rounded-full px-2 py-0.5">
            <XCircleIcon className="w-3 h-3 text-rose-500" />
            <span className="text-[10px] font-semibold text-rose-700">{rejected}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════ */
const PieChartDashboard = ({ user_id, user_name, role: propRole }) => {
  const [chartData, setChartData] = useState({
    totalCredit: 0, totalDebit: 0,
    totalCredit1: 0, totalDebit1: 0,
    totalCredit2: 0, totalDebit2: 0,
    netBalance: 0, threshold: 0, loading: true, error: null,
  });
  const [activeDataset, setActiveDataset] = useState('both');
  const [latestRequest, setLatestRequest] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [statusChartData, setStatusChartData] = useState({
    labels: ['In Progress', 'Approved', 'Rejected'],
    datasets: [{ data: [0, 0, 0], backgroundColor: ['#F59E0B', '#10B981', '#f43f5e'], borderWidth: 0 }],
  });

  const navigate = useNavigate();
  const userRole = propRole || localStorage.getItem('role') || 'none';
  const userId = user_id || localStorage.getItem('id');

  const handleViewRequest = () => {
    if (!latestRequest?.id) return;
    const ids = JSON.parse(localStorage.getItem('dismissedRequestIds') || '[]');
    if (!ids.includes(latestRequest.id)) {
      ids.push(latestRequest.id);
      localStorage.setItem('dismissedRequestIds', JSON.stringify(ids));
    }
    setLatestRequest(null);
    navigate('/SpecificWorkerReport', { state: { highlightedRequestId: latestRequest.id } });
  };

  const handleNotificationClick = (requestId) => {
    if (!requestId) return;
    const ids = JSON.parse(localStorage.getItem('viewedRequestIds') || '[]');
    if (!ids.includes(requestId)) { ids.push(requestId); localStorage.setItem('viewedRequestIds', JSON.stringify(ids)); }
    setShowNotifications(false);
    navigate('/RequestReport', { state: { highlightedRequestId: requestId } });
    setRecentRequests(prev => prev.filter(r => r.id !== requestId));
  };

  const fetchStatusChartData = async () => {
    if (userRole !== 'admin') return;
    try {
      const res = await axios.get(`${ApiURL}/getWorkerRequests.php`, { params: { user_id: userId, role: userRole }, withCredentials: true });
      const requests = res.data || [];
      const counts = { inprogress: 0, approved: 0, rejected: 0 };
      requests.forEach(r => { const s = r.status.toLowerCase(); if (s in counts) counts[s]++; });
      setStatusChartData({
        labels: ['In Progress', 'Approved', 'Rejected'],
        datasets: [{ data: [counts.inprogress, counts.approved, counts.rejected], backgroundColor: ['#F59E0B', '#10B981', '#f43f5e'], borderWidth: 0 }],
      });
    } catch (err) { console.error(err); }
  };

  const fetchChartData = async () => {
    setChartData(p => ({ ...p, loading: true }));
    try {
      const url = `${ApiURL}/PieChart.php?user_id=${userId}&user_name=${encodeURIComponent(user_name || '')}`;
      const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setChartData({
        totalCredit:  Number(data?.total?.credit  || 0),
        totalDebit:   Number(data?.total?.debit   || 0),
        totalCredit1: Number(data?.save?.credit   || 0),
        totalDebit1:  Number(data?.save?.debit    || 0),
        totalCredit2: Number(data?.submit?.credit || 0),
        totalDebit2:  Number(data?.submit?.debit  || 0),
        netBalance:   Number(data?.total?.net_balance || 0),
        threshold:    Number(data?.threshold || 0),
        loading: false, error: null,
      });
    } catch (err) {
      setChartData(p => ({ ...p, loading: false, error: `Failed to load: ${err.message}` }));
    }
  };

  const fetchRecentRequests = async () => {
    if (userRole !== 'admin' || !userId) return;
    try {
      const res = await axios.get(`${ApiURL}/getWorkerRequests.php`, { params: { user_id: userId, role: userRole }, withCredentials: true });
      const requests = Array.isArray(res.data) ? res.data : [];
      const threeDaysAgo = new Date(); threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      const viewedIds = JSON.parse(localStorage.getItem('viewedRequestIds') || '[]');
      const recent = requests
        .filter(r => ['pending','inprogress'].includes(r.status?.toLowerCase()) && new Date(r.created_at) >= threeDaysAgo && !viewedIds.includes(r.id))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentRequests(recent);
      setNotificationError(null);
    } catch (err) { setNotificationError(err.message); }
  };

  useEffect(() => {
    fetchChartData();
    if (userRole === 'admin') {
      fetchRecentRequests();
      fetchStatusChartData();
      const interval = setInterval(fetchRecentRequests, 30000);
      return () => clearInterval(interval);
    }
  }, [userRole, userId]);

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '₹0';

  const chartCards = [
    { title: 'All Transactions', subtitle: 'Total activity', credit: chartData.totalCredit, debit: chartData.totalDebit, accent: 'bg-gradient-to-r from-indigo-500 to-violet-500' },
    { title: 'Save Status', subtitle: 'Draft entries', credit: chartData.totalCredit1, debit: chartData.totalDebit1, accent: 'bg-gradient-to-r from-sky-400 to-cyan-500' },
    { title: 'Submit Status', subtitle: 'Submitted entries', credit: chartData.totalCredit2, debit: chartData.totalDebit2, accent: 'bg-gradient-to-r from-violet-500 to-purple-600' },
  ];

  /* summary stat cards */
  const totalCredit = Number(chartData.totalCredit) || 0;
  const totalDebit = Number(chartData.totalDebit) || 0;
  const netBalance = Number(chartData.netBalance) || 0;
  const threshold = Number(chartData.threshold) || 0;
  const showThresholdAlert = ['admin', 'worker'].includes(userRole) && threshold > 0 && netBalance <= threshold;

  return (
    <div className="space-y-5">

      {/* ── Summary stat pills ─────────────────────────────────────── */}
      {!chartData.loading && !chartData.error && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { label: 'Total Credit', value: formatCurrency(totalCredit), icon: ArrowTrendingUpIcon, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { label: 'Total Debit',  value: formatCurrency(totalDebit),  icon: ArrowTrendingDownIcon, color: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-100'    },
            { label: 'Net Balance',  value: formatCurrency(Math.abs(netBalance)), icon: ScaleIcon, color: netBalance >= 0 ? 'text-emerald-600' : 'text-rose-600', bg: netBalance >= 0 ? 'bg-emerald-50' : 'bg-rose-50', border: netBalance >= 0 ? 'border-emerald-100' : 'border-rose-100' },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className={`${s.bg} ${s.border} min-w-0 border rounded-2xl px-2 py-3 sm:px-4 flex flex-col items-center justify-center gap-1 text-center sm:flex-row sm:justify-start sm:gap-3 sm:text-left`}
            >
              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
                <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${s.color}`} />
              </div>
              <div className="min-w-0 max-w-full">
                <p className="truncate text-[8px] font-bold uppercase tracking-wide text-slate-500 sm:text-[10px]">{s.label}</p>
                <p className={`truncate text-xs font-bold sm:text-sm ${s.color}`}>{s.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showThresholdAlert && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <p className="text-sm font-semibold text-amber-800">
            Low balance alert: your current balance {formatCurrency(netBalance)} is at or below your threshold {formatCurrency(threshold)}.
          </p>
        </motion.div>
      )}

      {/* ── Main chart card ────────────────────────────────────────── */}
      <div className="pro-card overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-sm">
              <ChartPieIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 leading-tight">Transaction Insights</h2>
              <p className="text-[11px] text-slate-400">Real-time financial overview</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Filter pills */}
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
              {[
                { value: 'both', label: 'Both' },
                { value: 'credit', label: 'Credit' },
                { value: 'debit', label: 'Debit' },
              ].map(btn => (
                <button
                  key={btn.value}
                  onClick={() => setActiveDataset(btn.value)}
                  className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                    activeDataset === btn.value
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Bell */}
            {userRole === 'admin' && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`relative p-2 rounded-xl transition-all ${
                    recentRequests.length > 0
                      ? 'bg-rose-50 text-rose-500'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <BellIcon className="w-5 h-5" />
                  {recentRequests.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                      {recentRequests.length}
                    </span>
                  )}
                </button>
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                        <h3 className="text-sm font-bold text-slate-800">Recent Requests</h3>
                        <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-600">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                        {notificationError ? (
                          <div className="p-4 text-center text-xs text-rose-500">{notificationError}</div>
                        ) : recentRequests.length === 0 ? (
                          <div className="p-6 text-center text-xs text-slate-400">No new requests</div>
                        ) : recentRequests.map(req => (
                          <div
                            key={req.id}
                            onClick={() => handleNotificationClick(req.id)}
                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-semibold text-[13px] text-slate-800">{req.user_name || 'Unknown'}</p>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                {req.status === 'inprogress' ? 'In Progress' : 'Pending'}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500">Amount: {formatCurrency(req.amount)}</p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Latest request alert */}
        {latestRequest && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className={`mx-5 mt-4 p-3 rounded-xl flex items-center justify-between gap-3 ${
              latestRequest.status === 'approved'
                ? 'bg-emerald-50 border border-emerald-200'
                : 'bg-rose-50 border border-rose-200'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {latestRequest.status === 'approved'
                ? <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                : <XCircleIcon className="w-4 h-4 text-rose-600 flex-shrink-0" />
              }
              <p className={`text-[12px] font-medium truncate ${latestRequest.status === 'approved' ? 'text-emerald-800' : 'text-rose-800'}`}>
                Your request has been {latestRequest.status}
                {latestRequest.rejection_reason && ` — ${latestRequest.rejection_reason}`}
              </p>
            </div>
            <button
              onClick={handleViewRequest}
              className="bg-white text-slate-700 px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-slate-200 hover:bg-slate-50 transition-colors flex-shrink-0"
            >
              View
            </button>
          </motion.div>
        )}

        {/* Loading */}
        {chartData.loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <ArrowPathIcon className="w-7 h-7 text-indigo-400 animate-spin mb-3" />
            <p className="text-sm text-slate-400">Loading data…</p>
          </div>
        )}

        {/* Error */}
        {chartData.error && !chartData.loading && (
          <div className="mx-5 my-5 p-4 bg-rose-50 border border-rose-200 rounded-xl text-center">
            <p className="text-sm text-rose-600 mb-2">{chartData.error}</p>
            <button onClick={fetchChartData} className="text-indigo-600 text-sm font-semibold">Retry</button>
          </div>
        )}

        {/* Charts grid */}
        {!chartData.loading && !chartData.error && (
          <div className="p-4 sm:p-5">
            {/* 2 cols on mobile, 4 on sm+ */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
              {chartCards.map((card, idx) => (
                <motion.div key={card.title} transition={{ delay: idx * 0.08 }}>
                  <DonutCard {...card} activeDataset={activeDataset} />
                </motion.div>
              ))}
              <NetBalanceDonut balance={chartData.netBalance} />
              {userRole === 'admin' && (
                <StatusDonut
                  statusData={statusChartData}
                  onNavigate={(s) => navigate('/StatusRequests', { state: { selectedStatus: s } })}
                />
              )}
            </div>

            {/* footer */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center">
              <Link
                to="/SpecificWorkerReport"
                className="inline-flex items-center gap-2 text-[12px] font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-full transition-colors"
              >
                View My Requests →
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Worker notifications */}
      {userRole === 'worker' && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4">
          <WorkerNotifications onLatestRequestUpdate={setLatestRequest} />
        </div>
      )}
    </div>
  );
};

export default PieChartDashboard;
